import { QueryClient } from '@tanstack/react-query';
import { ApiError } from './errors';

/**
 * Single QueryClient instance for the app.
 *
 * Defaults chosen for a control-plane SPA (2024-2025 TanStack Query v5
 * guidance) and to address v1's most common bugs:
 *   - `staleTime: 30s` — list screens don't refetch on every mount; sidesteps
 *     the v5 default `staleTime: 0` that causes a refetch storm on remount.
 *   - `gcTime: 5min` — cached navigation back to a screen is instant.
 *   - `refetchOnWindowFocus: false` — the v5 default (`true`) is too noisy for
 *     an internal control plane where focus changes are constant.
 *   - `refetchOnReconnect: true` — after a network blip, re-sync (v5 default;
 *     kept explicit for intent).
 *   - `networkMode: 'online'` — the v5 default; queries pause while offline
 *     rather than firing doomed requests. We have no offline mode, so leave it.
 *   - `retry`: never retry 4xx (validation, auth, not-found are deterministic);
 *     cap network/5xx retries at 2 so a downed backend doesn't fan out.
 *   - mutations don't retry and bubble errors so callers decide the UI
 *     (toast vs. inline).
 *
 * These apply to *every* query/mutation in the app. This is the only
 * `QueryClient` — do not construct another (a second instance silently drops
 * these defaults and splits the cache). Override per query only with intent
 * (e.g. a logs view that sets `refetchInterval`, or static reference data that
 * sets a longer `staleTime`), never to "fix" a default globally.
 *
 * Perceived-load invariants for whoever wires the first real query hooks
 * (issue #74 — the app is still fixture-driven today, so none of these exist
 * yet):
 *   - Org-scoped queries MUST put `orgId` in the queryKey so the cache is
 *     partitioned per tenant; switching org then re-keys reads and refetches.
 *   - List/filter/tab/pagination queries that re-key on filters MUST set
 *     `placeholderData: keepPreviousData` (v5) so switching tabs/filters keeps
 *     the prior rows on screen instead of flashing empty.
 *   - Multi-query screens (e.g. the dashboard's parallel cards) should fetch in
 *     parallel as sibling `useQuery` calls. If suspense is adopted, switch to a
 *     single `useSuspenseQueries` so they stay parallel and don't waterfall.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
      networkMode: 'online',
      retry: (failureCount, error) => {
        if (error instanceof ApiError) {
          if (error.kind === 'network') return failureCount < 2;
          if (error.status >= 400 && error.status < 500) return false;
        }
        return failureCount < 2;
      },
    },
    mutations: {
      retry: false,
    },
  },
});
